import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useModal } from '@ebay/nice-modal-react';

// Import hooks
import { useSummaryStore } from 'components/views/offer_configurator/hooks';

// Import helpers
import { fetchSubscriptionCreate } from 'helpers/api/packetsSubscription';
import { openNotification } from 'components/utilities/notification/notification';
import { PAYMENT_PARAMS } from 'helpers/variables';
import { API_ERRORS } from 'helpers/error_code_api';
import { sendPacketsTransactionEvent, sendPacketsPaymentStartEvent } from 'helpers/tracking';

// Import routes
import { MAIN_PATHS } from 'components/routes/app_routes/paths';

// Import types
import { PaymentMethod } from 'types/api/payments';
import type { AxiosErrorResponse } from 'types/api/errors';
import type { TransactionLocationState } from 'types/views/transaction_finalization';
import { GtmPaymentStatus } from 'types/views/gtm';

const { OrderID } = PAYMENT_PARAMS;

export const usePayment = () => {
	const { t } = useTranslation('configurator');
	const history = useHistory();
	const summary = useSummaryStore(({ summary }) => summary);
	const modal = useModal();

	return useMutation({
		mutationFn: fetchSubscriptionCreate,
		onSuccess: (response, variables) => {
			const { redirect_url, order_id, message, price } = response.data.payment;

			const products = summary?.products ?? [];
			const product_title = products.map(({ title }) => title).join(', ');

			sendPacketsPaymentStartEvent(products, summary?.current_price);

			if (redirect_url) {
				window.location.replace(redirect_url);
			} else if (order_id) {
				history.push(`${MAIN_PATHS.PAYMENT_VERIFICATION}?${OrderID}=${order_id}`);
			} else if (!price) {
				const state: TransactionLocationState = { payment_method: PaymentMethod.PacketsSubscription, product_title, action: variables.action };

				sendPacketsTransactionEvent(products, 0, GtmPaymentStatus.Success);
				history.push({ pathname: MAIN_PATHS.PAYMENT_VERIFICATION, state });
			} else {
				openNotification({ type: 'info', title: message });
			}
		},
		onError: (error: AxiosErrorResponse) => {
			const errorCode = error?.response?.data?.errorCode;
			const shouldRedirect = errorCode === API_ERRORS.PACKETS_SUBSCRIPTION_ALREADY_EXIST || errorCode === API_ERRORS.PAYMENT_ALREADY_EXISTS;

			if (shouldRedirect) {
				history.replace(MAIN_PATHS.OFFER_CONFIGURATOR);
			}

			openNotification({
				type: 'error',
				title: t('notification_error_title'),
				description: t('notification_error_desc_payment')
			});
		},
		onSettled: modal.remove
	});
};
